<template>
    <div class="signature-pad">
        <canvas
            ref="canvas"
            class="signature-canvas w-100 border rounded bg-white"
            :class="{ 'opacity-8': locked }" />
        <!-- actions: a caller's own links on the pad's row, left of "Löschen" -->
        <div
            v-if="!locked"
            class="d-flex justify-content-between align-items-center gap-2 mt-1">
            <div>
                <slot name="actions" />
            </div>
            <base-button
                type="link"
                size="sm"
                class="shadow-none"
                @click="clear">
                {{ $t('core.clearSignature') }}
            </base-button>
        </div>
    </div>
</template>

<script setup>
const props = defineProps({
    locked: {
        type: Boolean,
        default: false
    }
})

const emit = defineEmits(['update:modelValue'])

const canvas = ref(null)
// Plain variable, not a ref: the pad is only ever driven imperatively and must not be made reactive.
let pad = null

function resizeCanvas() {
    if (!canvas.value) return
    const ratio = Math.max(window.devicePixelRatio || 1, 1)
    const data = pad && !pad.isEmpty() ? pad.toData() : null
    canvas.value.width = canvas.value.offsetWidth * ratio
    canvas.value.height = canvas.value.offsetHeight * ratio
    canvas.value.getContext('2d').scale(ratio, ratio)
    if (pad) {
        pad.clear()
        if (data) pad.fromData(data)
    }
}

function clear() {
    if (props.locked) return
    pad?.clear()
    emit('update:modelValue', '')
}

function emitSignature() {
    if (!pad || pad.isEmpty()) {
        emit('update:modelValue', '')
        return
    }
    emit('update:modelValue', pad.toDataURL('image/png'))
}

onMounted(async () => {
    const SignaturePad = (await import('signature_pad')).default
    resizeCanvas()
    pad = new SignaturePad(canvas.value, {backgroundColor: 'rgb(255, 255, 255)'})
    pad.addEventListener('endStroke', () => emitSignature())
    if (props.locked) pad.off()
    window.addEventListener('resize', resizeCanvas)
})

onBeforeUnmount(() => {
    window.removeEventListener('resize', resizeCanvas)
})

watch(() => props.locked, value => {
    if (!pad) return
    value ? pad.off() : pad.on()
})
</script>

<style lang="scss" scoped>
// Near a phone's width on every screen: a pad as wide as a desktop page invites a signature several times its usual size.
.signature-pad {
    max-width: 500px;
}

// The canvas needs an explicit height (it collapses otherwise) and no touch gestures: swiping a
// signature would scroll the page instead of drawing.
.signature-canvas {
    height: 200px;
    touch-action: none;
}
</style>
